import { AutomationSettings } from '@buyerflo/core/schemas/automation/settings'
import { useMutation } from '@tanstack/react-query'
import { useAutomationSettingsApi } from 'apiProviders'
import { useShowSnack } from 'components/snackbar'
import { useEffect, useState } from 'react'
import { SizableText, Switch, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'

type ToggleKey = keyof AutomationSettings

const automations: { key: ToggleKey; title: string; description: string }[] = [
    {
        key: 'autoEngagementInvoice',
        title: 'Auto-send engagement invoices',
        description: 'Generate and send the engagement fee invoice via Xero once a form is completed.',
    },
    {
        key: 'wishlistRequestOnIntake',
        title: 'Wishlist request on intake',
        description: "Email new clients a wishlist form automatically when they're added.",
    },
]

export default () => {
    const trpc = useTRPC()
    const settingsApi = useAutomationSettingsApi()
    const updateMutation = useMutation(trpc.automation.updateAutomationSettings.mutationOptions())
    const showSnack = useShowSnack()

    const [settings, setSettings] = useState<Required<AutomationSettings>>({
        autoEngagementInvoice: true,
        wishlistRequestOnIntake: false,
    })

    useEffect(() => {
        if (settingsApi.data) setSettings(settingsApi.data)
    }, [settingsApi.data])

    const toggle = async (key: ToggleKey, value: boolean) => {
        const previous = settings
        const next = { ...settings, [key]: value }
        setSettings(next)
        try {
            await updateMutation.mutateAsync(next)
            showSnack('Automation updated', 'success')
        } catch (e) {
            setSettings(previous)
            showSnack('Failed to update automation', 'danger')
            console.error(e)
        }
    }

    return (
        <YStack maxWidth={680}>
            <SizableText fontSize="$8" fontWeight="700">
                Automation
            </SizableText>
            <SizableText color="$color11" marginTop="$1">
                Control which tasks Buyerflo handles automatically.
            </SizableText>

            <YStack marginTop="$5">
                {automations.map(({ key, title, description }) => (
                    <XStack
                        key={key}
                        alignItems="center"
                        justifyContent="space-between"
                        gap="$4"
                        paddingVertical="$4"
                        borderTopWidth={1}
                        borderTopColor="$borderColor"
                    >
                        <YStack flex={1} gap="$1.5">
                            <SizableText fontWeight="600">{title}</SizableText>
                            <SizableText color="$color11" fontSize="$2" lineHeight="$1">
                                {description}
                            </SizableText>
                        </YStack>
                        <Switch
                            size="$3"
                            checked={settings[key]}
                            onCheckedChange={v => toggle(key, v)}
                        >
                            <Switch.Thumb animation="quick" />
                        </Switch>
                    </XStack>
                ))}
                <View borderTopWidth={1} borderTopColor="$borderColor" />
            </YStack>
        </YStack>
    )
}
